import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, of } from 'rxjs';

import { BiometriaService } from '@/core/api/sicom-api/registro/medico/biometria/biometria.service';
import { type TablePerfilValue } from '@/shared/components/layout/custom-table/custom-table.component';

/** Chave do cache: CRM e UF identificam o médico juntos — CRM repete entre conselhos. */
function chaveDaFoto(crm: string, uf: string): string {
    return `${crm.trim()}|${uf.trim().toUpperCase()}`;
}

/** Iniciais para o círculo quando não há retrato. Duas, no máximo — mais que isso não cabe. */
export function iniciaisDoNome(nome: string | undefined): string {
    const partes = (nome ?? '')
        .trim()
        .split(/\s+/)
        .filter((parte) => parte.length > 0);

    if (!partes.length) {
        return '';
    }

    const primeira = partes[0][0] ?? '';
    const ultima = partes.length > 1 ? (partes[partes.length - 1][0] ?? '') : '';
    return (primeira + ultima).toUpperCase();
}

/**
 * Retratos dos médicos listados na Central de Transferência e no arquivo de protocolos de serviços.
 *
 * <p>Serviço, e não um trecho repetido em cada tela: as abas de uma mesma tela listam a mesma
 * população de médicos e um cache por componente faria a mesma foto ser baixada de novo a cada
 * troca de aba. Aqui a busca acontece uma vez por CRM/UF enquanto a tela viver.
 *
 * <p>Foto é enfeite: falha de rede vira "sem retrato" (iniciais), nunca erro na tela — o que
 * interessa ali é a solicitação, não a imagem.
 */
@Injectable()
export class FotosMedicosService {
    private readonly biometriaService = inject(BiometriaService);
    private readonly destroyRef = inject(DestroyRef);

    /** `null` marca "já buscamos e não há foto", o que impede repetir a consulta. */
    private readonly fotos = signal<Map<string, string | null>>(new Map());

    /** Retrato e nome prontos para a coluna `perfil`. Nome vazio faz a coluna cair no `default`. */
    perfilDoMedico(nome: string | undefined, crm: string | undefined, uf: string | undefined): TablePerfilValue {
        const iniciais = iniciaisDoNome(nome);
        if (!iniciais) {
            return { nome: '', foto: null, iniciais: null };
        }

        return { nome: nome ?? '', foto: this.fotoPorCrmUf(crm, uf), iniciais };
    }

    /**
     * Retrato de um CRM/UF, ou `null` enquanto não chegou (ou se não há foto cadastrada). Público
     * porque nem todo consumidor quer o par retrato+nome de `perfilDoMedico`: no arquivo de
     * serviços o retrato é de uma pessoa e o nome exibido ao lado é o de uma empresa.
     */
    fotoPorCrmUf(crm: string | undefined, uf: string | undefined): string | null {
        if (!crm || !uf) {
            return null;
        }
        return this.fotos().get(chaveDaFoto(crm, uf)) ?? null;
    }

    /** Busca o que ainda não está em cache. Chamada a cada carga/página da tabela. */
    carregar(medicos: readonly { crm?: string; uf?: string }[]): void {
        const pendentes = new Map<string, { crm: string; uf: string }>();

        for (const medico of medicos) {
            const crm = (medico.crm ?? '').trim();
            const uf = (medico.uf ?? '').trim();
            if (!crm || !uf) {
                continue;
            }
            const chave = chaveDaFoto(crm, uf);
            if (!this.fotos().has(chave) && !pendentes.has(chave)) {
                pendentes.set(chave, { crm, uf });
            }
        }

        pendentes.forEach((alvo, chave) => {
            this.biometriaService
                .buscarFotoPorCrmUf(alvo.crm, alvo.uf)
                .pipe(
                    catchError(() => of({ imagemBase64: undefined })),
                    takeUntilDestroyed(this.destroyRef)
                )
                .subscribe((resposta) => {
                    const imagem = resposta?.imagemBase64 ? `data:image/png;base64,${resposta.imagemBase64}` : null;
                    const mapa = new Map(this.fotos());
                    mapa.set(chave, imagem);
                    // O getter que monta a linha lê `fotos()`; trocar o mapa já redesenha a tabela,
                    // sem sinal de versão à parte.
                    this.fotos.set(mapa);
                });
        });
    }
}
